import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FilterMatchMode } from "primereact/api";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { Column } from "iuvity-ui-components/dist/column";
import { FormSkeleton } from "../../components";
import { TableHeader } from "../../components/datatable/TableHeader";
import { Actions } from "../../components/datatable/Actions";
import { TransactionCreator } from "../../components/transactions/TransactionCreator";
import { DialogProps } from "../dialog/CustomDialog";
import { Transaction } from "../../interfaces";
import { AppContext } from "../../context/index";
import { allFieldsBodyTemplate } from "../../components/datatable/allFieldsBodyTemplate";
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";

interface TransactionInfoProps {
  data: null | any[];
  options: any;
  actionFn: Function;
  reloadFn: Function;
  auxModalController: React.Dispatch<React.SetStateAction<DialogProps>>;
}

export const TransactionsInfo: React.FC<TransactionInfoProps> = ({
  data,
  actionFn,
  reloadFn,
  options,
  auxModalController,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t,
    appState: { language, permissiondInfoByRole },
    setAppState,
  } = useContext(AppContext);

  const transactionTypesColumns = useMemo(() => {
    if (language) {
      return [
        {
          field: "id",
          header: "Id",
        },
        {
          field: "name",
          header: t("Transactions.nameLabel"),
        },
        {
          field: "labelkey",
          header: t("Transactions.labelkeyLabel"),
        },
        {
          field: "type",
          header: t("Transactions.typeLabel"),
        },
        {
          field: "authorizationflowname",
          header: t("Transactions.authorizationflownameLabel"),
        },
        {
          field: "isbatch",
          header: t("Transactions.isbatchLabel"),
        },
        {
          field: "status",
          header: t("Transactions.statusLabel"),
        },
        {
          field: "releaseversion",
          header: t("Transactions.releaseversionLabel"),
        },
        {
          field: "actions",
          header: t("Transactions.actionsLabel"),
        },
      ];
    } else {
      return [];
    }
  }, [t, language]);

  const [visible, setVisible] = useState<boolean>(false);
  const [selectedColumns, setSelectedColumns] = useState(
    transactionTypesColumns
  );
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const onEditAction = (transaction: Transaction) => {
    setAppState((prev) => ({
      ...prev,
      selectedTransaction: transaction,
    }));
    sessionStorage.removeItem("TRANSACTION-FV");
    navigate("/transactions/configuration");
  };

  const onDeleteAction = async (transaction: Transaction) => {
    await actionFn("DELETE", transaction.id);
    reloadFn();
  };

  const handleAuxModal = async (transaction: Transaction) => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: t("Transactions.deleteModalTitle"),
      content: `${t("Transactions.deleteModalContent1")} "${transaction?.name}"
      ${t("Transactions.deleteModalContent2")}`,
      onConfirm: () => onDeleteAction(transaction),
    }));
  };

  useEffect(() => {
    if (language) {
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(`Transactions.${col.field}Label`),
        }))
      );
    }
  }, [t, language, setSelectedColumns]);

  return (
    <>
      <SubheaderActions
        className="px-0 pt-4 pb-3"
        actions={subheaderActionsPrivilegeValidator([
          {
            id: "111",
            icon: "iu iu-icon-plus",
            label: t("Transactions.newTransactionTitle"),
            style: {
              display: !permissiondInfoByRole.CREATE ? 'none' : undefined,
            },
            onClick: () => navigate(`${location.pathname}/new`),
          },
        ])}
      />

      {data ? (
        <DataTable
          header={
            <TableHeader
              title={t("Transactions.transactionAdminTitle")}
              columns={transactionTypesColumns}
              selectedColumns={selectedColumns}
              toggleColumns={setSelectedColumns}
              filters={filters}
              setFilters={setFilters}
              reload={reloadFn}
            />
          }
          currentPageReportTemplate={t(
            "ConsoleTranslations.rowsPaginatorTemplateLabel"
          )}
          paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
          emptyMessage={t("TableActions.emptyMessage")}
          rows={10}
          size="normal"
          filters={filters}
          sortField="id"
          dataKey="id"
          sortOrder={1}
          value={data.map((transaction) => {
            const isbatch = transaction.isbatch
              ? t("Transactions.yesLabel")
              : "NO";
            return {
              ...transaction,
              isbatch,
              type: t(
                `Transactions.${transaction?.type.toLocaleLowerCase()}TypeLabel`
              ),
              status: t(
                `Transactions.${transaction?.status.toLocaleLowerCase()}StatusLabel`
              ),
              actions: (
                <Actions
                  enableDelete={!!transaction.createdby}
                  onEdit={() => onEditAction(transaction)}
                  onDelete={() => handleAuxModal(transaction)}
                  visibleObject={{...permissiondInfoByRole}}
                />
              ),
            };
          })}
          scrollable
          resizableColumns
        >
          {selectedColumns.map((col) => {
            return (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable={col.field !== "actions"}
                align={col.field === "actions" ? "center" : "left"}
                headerStyle={col.field === "actions"
                  ? {
                      justifyContent: "center",
                      display: (!permissiondInfoByRole.UPDATE && !permissiondInfoByRole.REMOVE) ? 'none' : undefined,
                    }
                  : undefined }
                style={{
                  boxShadow: (col.field === "actions")
                    ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                    : undefined,
                  maxWidth: (col.field === "actions") ? "132px" : undefined,
                  whiteSpace: "normal",
                  minWidth: (col.field !== "actions") ? "10rem" : "",
                  display: (col.field === "actions")
                    ? ((!permissiondInfoByRole.UPDATE && !permissiondInfoByRole.REMOVE) ? 'none' : undefined)
                    : undefined,
                }}
                frozen={col.field === "actions" ? true : false}
                alignFrozen={col.field === "actions" ? "right" : undefined}
                body={(rowData) => allFieldsBodyTemplate(rowData, col.field, 'createdby')}
              />
            );
          })}
        </DataTable>
      ) : (
        <FormSkeleton length={8} />
      )}

      <TransactionCreator
        visible={visible}
        setVisible={setVisible}
        createFn={actionFn}
        reloadFn={reloadFn}
        options={options}
        auxModalController={auxModalController}
      />
    </>
  );
};
